'use client'

import { useEffect } from 'react'

// Signals that a real user is present and should trigger loading the chat. mousemove
// fires almost immediately for desktop visitors (well before any scroll/click), so the
// widget appears quickly; the rest cover touch and keyboard.
const EVENTS = ['mousemove', 'pointerdown', 'keydown', 'touchstart', 'scroll'] as const

/**
 * Loads the Mitel webchat bootstrapper on the first sign of a real user (mouse
 * movement, touch, scroll or key press) or after an idle fallback, instead of during
 * page load. The chat widget is heavy (~380 KB) and is never needed at first paint, so
 * keeping it off the critical path removes it from Total Blocking Time. Automated tools
 * (PageSpeed/bots) don't move a mouse or interact, so it only loads via the idle
 * fallback, well after the measured window.
 */
export function WebChatLoader({ src }: { src: string }) {
  useEffect(() => {
    let done = false

    const load = () => {
      if (done) return
      done = true
      EVENTS.forEach((event) => window.removeEventListener(event, load))
      const script = document.createElement('script')
      script.src = src
      script.async = true
      document.body.appendChild(script)
    }

    EVENTS.forEach((event) => window.addEventListener(event, load, { passive: true }))
    // Fallback so the widget still appears for users who never interact.
    const timer = setTimeout(load, 8000)

    return () => {
      done = true
      EVENTS.forEach((event) => window.removeEventListener(event, load))
      clearTimeout(timer)
    }
  }, [src])

  return null
}
